import { Checkbox, DefaultButton, Dialog, DialogType, Label, MessageBar, MessageBarType, PrimaryButton, Stack } from '@fluentui/react';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import BlockUi from 'react-block-ui';
import 'react-block-ui/style.css';
import { useDispatch, useSelector } from 'react-redux';
import { HealAPI } from '../../../healApi';
import { toast } from '../../../redux/message/actions';
import { RootState } from '../../../redux/types';
import styles from './CancelUnconfirmedVisitsDialog.module.scss';

interface Props {
  onSuccess?: () => void;
  onCancel?: () => void;
  hidden?: boolean;
}

type SelectedMarketGroups = { [group: string]: boolean };

const CancelUnconfirmedVisitsDialog: React.FC<Props> = ({ onSuccess, onCancel, hidden }) => {
  const dispatch = useDispatch();
  const { date } = useSelector((state: RootState) => state.ui);
  const { markets } = useSelector((state: RootState) => state.markets);

  const regionGroups = useMemo(() => {
    return _.mapValues(_.groupBy(markets.data, 'region'), (markets) => {
      return Object.keys(_.groupBy(markets, 'groupCode'));
    });
  }, [markets.data]);

  const marketGroups = useMemo(() => _.groupBy(markets.data, 'groupCode'), [markets.data]);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Reducer function for initializing & toggling selected market groups
  const selectedGroupsReducer = (state: SelectedMarketGroups, action: { group?: string }) => {
    // Initialize
    if (!action?.group) {
      const groups: SelectedMarketGroups = {};
      Object.keys(marketGroups).forEach((marketGroup) => (groups[marketGroup] = false));
      return groups;
    }

    // Toggle a market group
    return {
      ...state,
      [action.group]: !state[action.group],
    };
  };

  // State for which market groups are selected
  const [selectedGroups, dispatchToggleGroup] = useReducer(selectedGroupsReducer, {});

  // Array of selected market group keys
  const selectedGroupKeys = useMemo(
    () =>
      Object.entries(selectedGroups)
        .filter((group) => group[1])
        .map((group) => group[0]),
    [selectedGroups]
  );

  // Initialize state object once markets have loaded
  useEffect(() => {
    if (Object.keys(marketGroups).length && !Object.keys(selectedGroups).length) {
      dispatchToggleGroup({});
    }
  }, [marketGroups, selectedGroups]);

  // Reset isConfirming & selected market groups when shown
  // NOTE: Resetting when hiding causes the dialog to briefly change state as it animates out
  useEffect(() => {
    if (!hidden) {
      setIsConfirming(false);
      dispatchToggleGroup({});
    }
  }, [hidden]);

  const cancelUnconfirmedVisits = useCallback(async () => {
    if (selectedGroupKeys.length) {
      setIsSaving(true);
      const response = await HealAPI.cancelUnconfirmedVisits(date, selectedGroupKeys);

      if (response.ok && response.data?.data) {
        dispatch(toast({ type: 'success', title: 'Success', content: `Unconfirmed visits for ${date} have been canceled` }));
        onSuccess && onSuccess();
      } else {
        dispatch(toast({ type: 'error', title: 'Error', content: 'There was an error attempting to cancel unconfirmed visits' }));
      }

      setIsSaving(false);
    }
  }, [date, selectedGroupKeys, onSuccess, dispatch]);

  const renderMessageBar = () => {
    return (
      <MessageBar messageBarType={isConfirming ? MessageBarType.severeWarning : MessageBarType.warning} isMultiline dismissButtonAriaLabel="Close">
        This will cancel all visits scheduled for <strong>{date}</strong> in the {selectedGroupKeys.length ? <strong>{selectedGroupKeys.join(', ')}</strong> : 'selected'} market
        group{selectedGroupKeys.length !== 1 && 's'} that the patient has not confirmed.
      </MessageBar>
    );
  };

  return (
    <Dialog hidden={hidden} dialogContentProps={{ type: DialogType.largeHeader, title: `Cancel unconfirmed visits for ${date}` }} onDismiss={onCancel}>
      <BlockUi tag="div" blocking={isSaving}>
        {isConfirming ? (
          <>
            <div className={styles.confirmTitle}>Are you sure you want to proceed?</div>
            {renderMessageBar()}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <PrimaryButton onClick={cancelUnconfirmedVisits} disabled={!selectedGroupKeys.length} data-tid="btn_yesCancelVisits" className={styles.submitBtn}>
                Yes, cancel visits
              </PrimaryButton>
              <DefaultButton onClick={() => setIsConfirming(false)} data-tid="btn_noGoBack">
                No, go back
              </DefaultButton>
            </div>
          </>
        ) : (
          <>
            <Label>Select market group(s):</Label>
            <Stack style={{ marginBottom: 15 }}>
              {Object.entries(regionGroups).map((regionGroup, index) => (
                <div style={{ marginBottom: 10 }} key={index}>
                  <Label style={{ marginBottom: 5, color: '#0078d4' }}>{regionGroup[0] === 'null' ? 'Other' : regionGroup[0]}</Label>
                  <Stack horizontal wrap gap="20 0">
                    {regionGroup[1].map((group) => (
                      <Checkbox label={group} checked={selectedGroups[group]} onChange={() => dispatchToggleGroup({ group })} key={group} styles={{ root: { width: '16.6%' } }} />
                    ))}
                  </Stack>
                </div>
              ))}
            </Stack>

            {renderMessageBar()}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <PrimaryButton onClick={() => setIsConfirming(true)} disabled={!selectedGroupKeys.length} data-tid="btn_cancelVisits">
                Continue
              </PrimaryButton>
              <DefaultButton onClick={onCancel} data-tid="btn_nevermind">
                Nevermind
              </DefaultButton>
            </div>
          </>
        )}
      </BlockUi>
    </Dialog>
  );
};

export default CancelUnconfirmedVisitsDialog;
